import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
    Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Chip, CircularProgress, Dialog, DialogContent, IconButton,
    Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography, useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/en-gb';
import { getClientSubTaskVisits, requestVisitDateChange } from '../../../Redux/actions';
import { THEME_COLOR, buttonStyles, datePickerSlotProps } from '../../../theme/formStyles';
import {
    dayNumber, engineerName, fmtDate, fmtDuration, fmtTime, isOnDay, isOpenVisit, subTaskStatusStyle, taskLength, todayYmd, visitMinutes,
} from '../projectSchedule.utils';
import { TimesGrid } from '../schedule/VisitPanel';
import { downloadVisitReport, printVisitReport } from '../schedule/visitReport';

const sectionSx = { fontSize: '11.5px', fontWeight: 700, color: '#6b7785', textTransform: 'uppercase', letterSpacing: '.05em', mt: 2.25, mb: 0.75 };
const fieldSx = { '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: THEME_COLOR }, '& .MuiInputLabel-root.Mui-focused': { color: THEME_COLOR } };

function RequestNote({ request, task }) {
    if (!request) return null;
    if (request.status === 'pending') {
        return <Alert severity="warning" sx={{ mt: 1.5 }}>You asked to move this visit to <b>{fmtDate(request.requested_date, 'ddd D MMM')}</b>. Waiting for Greenvironment to confirm.</Alert>;
    }
    if (request.status === 'approved') {
        return <Alert severity="success" sx={{ mt: 1.5 }}>Date change approved. The visit is now on <b>{fmtDate(task.start_date, 'ddd D MMM')}</b>.</Alert>;
    }
    return <Alert severity="info" sx={{ mt: 1.5 }}>Your request to move this visit was declined{request.response_note ? `: ${request.response_note}` : ''}. The original date stays.</Alert>;
}

// One visit as the client sees it: read-only status and times, reports once the engineer checks out,
// and a request for another date while the visit is still open.
export default function ClientVisitDialog({ task, request, onClose, onRequested }) {
    const dispatch = useDispatch();
    const fullScreen = useMediaQuery('(max-width:700px)');
    const [visits, setVisits] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [form, setForm] = useState(null); // { date: dayjs, reason }
    const [sending, setSending] = useState(false);

    const load = useCallback(() => {
        setLoading(true);
        dispatch(getClientSubTaskVisits(task.id)).then((res) => {
            if (res?.data?.success) setVisits(res.data.data || []);
            else setError(res?.data?.message || 'Could not load the visit log');
            setLoading(false);
        });
    }, [dispatch, task.id]);

    useEffect(() => { load(); }, [load]);

    const today = todayYmd();
    const len = taskLength(task);
    const scheduledToday = isOnDay(task, today);
    const todayVisit = visits.find(v => v.visit_date === today);
    const finished = visits.filter(v => v.check_out_at);
    const total = visits.reduce((s, v) => s + visitMinutes(v), 0);
    // Once the engineer has checked in the work is under way; the server refuses a request then too
    const started = visits.length > 0;
    const canRequest = !loading && !started && !['Completed', 'Cancelled'].includes(task.status) && request?.status !== 'pending';

    const sendRequest = () => {
        if (!form?.date) { setError('Pick the date you would like instead'); return; }
        setSending(true); setError(null);
        dispatch(requestVisitDateChange(task.id, { requested_date: form.date.format('YYYY-MM-DD'), reason: form.reason || '' })).then((res) => {
            setSending(false);
            if (res?.data?.success) { setForm(null); onRequested(); }
            else setError(res?.data?.message || 'Could not send the request');
        });
    };

    return (
        <Dialog open onClose={onClose} fullWidth maxWidth="sm" fullScreen={fullScreen} PaperProps={{ sx: { borderRadius: fullScreen ? 0 : 4 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2.25, py: 1.75, bgcolor: '#e6f4f2', color: THEME_COLOR }}>
                <Typography sx={{ fontWeight: 700, flex: 1, minWidth: 0 }} noWrap>{task.task_name || 'Site visit'}</Typography>
                <Chip size="small" label={todayVisit && isOpenVisit(todayVisit) ? '● On site' : task.status}
                    sx={{ fontWeight: 700, bgcolor: '#fff', color: todayVisit && isOpenVisit(todayVisit) ? '#16a34a' : subTaskStatusStyle(task.status).color }} />
                <IconButton size="small" onClick={onClose} aria-label="Close" sx={{ color: THEME_COLOR }}><CloseIcon /></IconButton>
            </Box>
            <DialogContent sx={{ pt: 2 }}>
                <Typography sx={{ fontSize: '19px', fontWeight: 500, mb: 0.5 }}>{task.sub_task_name}</Typography>
                <Typography sx={{ color: '#6b7785', fontSize: '13.5px' }}>
                    📅 {len === 1 ? fmtDate(task.start_date) : `${fmtDate(task.start_date)} – ${fmtDate(task.end_date)} · ${len} days`}
                    {len > 1 && scheduledToday && ` · day ${dayNumber(task, today)} of ${len}`}
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, border: '1px solid #e5e9ee', borderRadius: 3, p: 1.25, mt: 1.75 }}>
                    <Box sx={{ width: 38, height: 38, borderRadius: '50%', display: 'grid', placeItems: 'center', fontSize: '13px', fontWeight: 700, bgcolor: '#dbe7ff', color: '#1d64d8', flex: 'none' }}>
                        {task.spoc ? engineerName(task).split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase() : '?'}
                    </Box>
                    <Box>
                        <Typography sx={{ fontWeight: 500 }}>{task.spoc ? engineerName(task) : 'Engineer to be assigned'}</Typography>
                        <Typography sx={{ fontSize: '12px', color: '#6b7785' }}>{task.spoc ? 'Greenvironment engineer' : 'Name shared before the visit'}</Typography>
                    </Box>
                </Box>

                {scheduledToday && (
                    <>
                        <Typography sx={sectionSx}>Today's site visit · {fmtDate(today)}</Typography>
                        <Box sx={{ borderRadius: 3.5, p: 1.75, background: 'linear-gradient(135deg,#f3fbf9,#fff)', border: '1px solid #cfe9e3' }}>
                            <Typography sx={{ fontSize: '13px', color: '#6b7785', mb: 1 }}>
                                {!todayVisit ? "The engineer hasn't checked in yet"
                                    : isOpenVisit(todayVisit) ? <span style={{ color: '#16a34a', fontWeight: 700 }}>● The engineer is on site</span>
                                    : 'Visit finished for today'}
                            </Typography>
                            <TimesGrid visit={todayVisit} />
                            {todayVisit?.check_out_at && (
                                <Button size="small" variant="outlined" sx={buttonStyles.outlined} onClick={() => downloadVisitReport(task, visits, today)}>⬇ Today's report</Button>
                            )}
                            {todayVisit && !todayVisit.check_out_at && (
                                <Typography sx={{ fontSize: '12px', color: '#6b7785' }}>The visit report with photos appears here after the engineer checks out.</Typography>
                            )}
                        </Box>
                    </>
                )}

                <RequestNote request={request} task={task} />

                {task.description && (
                    <Accordion disableGutters sx={{ mt: 1.75, border: '1px solid #e5e9ee', boxShadow: 'none', borderRadius: '12px !important', '&:before': { display: 'none' } }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography sx={{ fontWeight: 500, fontSize: '13.5px' }}>What we'll do</Typography></AccordionSummary>
                        <AccordionDetails sx={{ pt: 0 }}><Typography sx={{ fontSize: '13.5px', whiteSpace: 'pre-wrap' }}>{task.description}</Typography></AccordionDetails>
                    </Accordion>
                )}

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={22} sx={{ color: THEME_COLOR }} /></Box>
                ) : visits.length > 0 && (
                    <>
                        <Typography sx={sectionSx}>
                            Site visit log <span style={{ textTransform: 'none', letterSpacing: 0, fontWeight: 500 }}>· {visits.length} visit{visits.length === 1 ? '' : 's'} · {fmtDuration(total)}</span>
                        </Typography>
                        <Box sx={{ overflowX: 'auto' }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>{['Date', 'Arrived', 'Left', 'Hours', ''].map(h => <TableCell key={h} sx={{ fontSize: '11px', color: '#6b7785', fontWeight: 700, textTransform: 'uppercase', px: 0.5 }}>{h}</TableCell>)}</TableRow>
                                </TableHead>
                                <TableBody>
                                    {visits.map(v => (
                                        <TableRow key={v.id} sx={v.visit_date === today ? { bgcolor: '#f3fbf9' } : undefined}>
                                            <TableCell sx={{ px: 0.5, whiteSpace: 'nowrap' }}>{fmtDate(v.visit_date, 'ddd D MMM')}</TableCell>
                                            <TableCell sx={{ px: 0.5 }}>{fmtTime(v.check_in_at)}</TableCell>
                                            <TableCell sx={{ px: 0.5 }}>{v.check_out_at ? fmtTime(v.check_out_at) : <span style={{ color: '#16a34a', fontWeight: 700 }}>on site</span>}</TableCell>
                                            <TableCell sx={{ px: 0.5 }}>{fmtDuration(visitMinutes(v))}</TableCell>
                                            <TableCell sx={{ px: 0.5, textAlign: 'right' }}>
                                                {v.check_out_at && <Button size="small" sx={{ color: THEME_COLOR, textTransform: 'none', minWidth: 0 }} onClick={() => downloadVisitReport(task, visits, v.visit_date)}>⬇ Report</Button>}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                        {finished.length > 0 && (
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.5 }}>
                                <Button size="small" variant="outlined" sx={buttonStyles.outlined} onClick={() => downloadVisitReport(task, visits)}>⬇ Download full report</Button>
                                <Button size="small" variant="outlined" sx={buttonStyles.outlined} onClick={() => printVisitReport(task, visits)}>🖨 Print / Save PDF</Button>
                            </Box>
                        )}
                    </>
                )}

                {form && (
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
                        <Box sx={{ border: '1px solid #e5e9ee', borderRadius: 3, p: 1.5, mt: 2 }}>
                            <Typography sx={{ fontWeight: 700, fontSize: '13.5px', mb: 1.25 }}>📅 Request another date</Typography>
                            <DatePicker label="Preferred date *" format="DD/MM/YYYY" value={form.date} minDate={dayjs().add(1, 'day')}
                                onChange={(v) => setForm(f => ({ ...f, date: v }))} slotProps={datePickerSlotProps({ fullWidth: true, size: 'small' })} />
                            <TextField fullWidth multiline minRows={2} size="small" label="Reason (optional)" placeholder="e.g. Plant shutdown that day"
                                value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))} inputProps={{ maxLength: 1000 }} sx={{ ...fieldSx, mt: 1.5 }} />
                            <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
                                <Button variant="contained" sx={buttonStyles.contained} disabled={sending} onClick={sendRequest}>
                                    {sending ? <CircularProgress size={18} color="inherit" /> : 'Send request'}
                                </Button>
                                <Button variant="outlined" sx={buttonStyles.outlined} disabled={sending} onClick={() => setForm(null)}>Cancel</Button>
                            </Box>
                        </Box>
                    </LocalizationProvider>
                )}

                {error && <Alert severity="error" sx={{ mt: 1.5 }} onClose={() => setError(null)}>{error}</Alert>}

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2.5 }}>
                    {canRequest && !form && (
                        <Button variant="outlined" sx={buttonStyles.outlined}
                            onClick={() => setForm({ date: dayjs(task.start_date).add(2, 'day').isAfter(dayjs()) ? dayjs(task.start_date).add(2, 'day') : dayjs().add(1, 'day'), reason: '' })}>
                            Request another date
                        </Button>
                    )}
                    <Button variant="outlined" sx={buttonStyles.outlined} onClick={onClose}>Close</Button>
                </Box>
                {started && !['Completed', 'Cancelled'].includes(task.status) && (
                    <Typography sx={{ fontSize: '12px', color: '#6b7785', mt: 1 }}>This visit has started, so its date can no longer be changed here. Please contact the team.</Typography>
                )}
            </DialogContent>
        </Dialog>
    );
}
